'use client';

import { useAtomRefresh, useAtomSet, useAtomValue } from '@effect/atom-react';
import {
  GlossaryIndex,
  GlossaryIndexColumns,
  GlossaryIndexCount,
} from '@vs-shop/commerce-ui/glossaryIndex';
import { Chunk, Option } from 'effect';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import { useIntl } from 'react-intl';

import { glossaryTermsPerPage } from '@vs-shop/engine/config/glossary';

import { GlossaryAlphabetListPerPage } from './GlossaryAlphabetListPerPage';
import { GlossaryEmptyState } from './GlossaryEmptyState';
import { GlossaryLoadError } from './GlossaryLoadError';
import { GlossarySkeletonGrid } from './GlossarySkeletonGrid';
import LoadMorePagination from '../../../../container/pagination/LoadMorePagination';

import { fetchGlossaryTermsAtom } from '../../../../effect/atom/glossary/fetchGlossaryTerms';
import { GlossaryMode } from '../../../../effect/data/glossary/glossaryMode';
import { glossaryLoadMorePagesAtom } from '../../../../effect/atom/glossary/glossaryPagination';

type Props = {
  alphabetFilter: string;
};

export function GlossaryAlphabetList({ alphabetFilter }: Props) {
  const intl = useIntl();
  const loadMorePagesAtom = glossaryLoadMorePagesAtom(alphabetFilter);
  const loadMore = useAtomValue(loadMorePagesAtom);
  const setLoadMorePages = useAtomSet(loadMorePagesAtom, { mode: 'value' });

  const firstPageAtom = fetchGlossaryTermsAtom(
    alphabetFilter,
    null,
    false,
    1,
    glossaryTermsPerPage
  );
  const firstPageResponse = useAtomValue(firstPageAtom);
  const retryFirstPage = useAtomRefresh(firstPageAtom);

  return AsyncResult.builder(firstPageResponse)
    .onInitial(() => <GlossarySkeletonGrid />)
    .onFailure(() => <GlossaryLoadError onRetry={retryFirstPage} />)
    .onSuccess((response) => {
      const totalItems = response['hydra:totalItems'];
      if (totalItems === 0) {
        return (
          <GlossaryEmptyState
            mode={GlossaryMode.alphabet}
            letter={alphabetFilter}
            searchQuery={null}
          />
        );
      }

      const hydraViewOption = Option.fromNullishOr(response['hydra:view']);

      return (
        // The mode this branch is, re-supplied by the tenant: the three
        // runs are one shared block, and which one server-rendered is a claim
        // about this page rather than about the block.
        <GlossaryIndex data-mode="alphabet">
          <GlossaryIndexCount>
            {intl.formatMessage(
              {
                id: 'component.client.page.glossary.alphabet.count',
                defaultMessage:
                  '{count, plural, one {Nalezen # pojem} few {Nalezeny # pojmy} other {Nalezeno # pojmů}}',
              },
              { count: totalItems }
            )}
          </GlossaryIndexCount>

          <GlossaryIndexColumns>
            {Chunk.range(1, 1 + loadMore).pipe(
              Chunk.map((page) => (
                <GlossaryAlphabetListPerPage
                  key={`page-${String(page)}`}
                  alphabetFilter={alphabetFilter}
                  page={page}
                />
              )),
              Chunk.toReadonlyArray
            )}
          </GlossaryIndexColumns>

          {Option.match(hydraViewOption, {
            onNone: () => null,
            onSome: (hydraViewExtended) => (
              <LoadMorePagination
                hydraViewExtended={hydraViewExtended}
                lastPageLoaded={1 + loadMore}
                loadMorePages={() => {
                  setLoadMorePages((pages) => pages + 1);
                }}
              />
            ),
          })}
        </GlossaryIndex>
      );
    })
    .render();
}
